import { themeAtom } from "../../../shared/atoms";
import { log, logE } from "../../../shared/services/debug-logger";
import {
  AdminSeniorVoMember,
  AdminSeniorVoTeam,
} from "../../models/admin-senior-vo.models";
import {
  addAdminSeniorVoMember,
  addAdminSeniorVoTeam,
  getAdminSeniorVoErrorMessage,
  getAdminSeniorVoTeams,
  updateAdminSeniorVoMemberRole,
  updateAdminSeniorVoMemberStatus,
} from "../../services/admin-senior-vo.service";
import { adminStyles } from "../../styles/admin.styles";
import AdminSeniorVoAddMemberModal, {
  AdminSeniorVoAddMemberFormErrors,
  AdminSeniorVoAddMemberFormValues,
} from "./AdminSeniorVoAddMemberModal";
import AdminSeniorVoAddTeamModal, {
  AdminSeniorVoAddTeamFormErrors,
  AdminSeniorVoAddTeamFormValues,
} from "./AdminSeniorVoAddTeamModal";
import AdminSeniorVoResponseModal, { AdminSeniorVoResponseStatus } from "./AdminSeniorVoResponseModal";
import AdminVoyagePnlViewTabs from "./AdminVoyagePnlViewTabs";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import type { FormEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

type SeniorVoRoleFilter = "All" | "Senior VO" | "VO Member";
type SeniorVoStatusFilter = "All" | "Active" | "Inactive";
type LoadingState = "loading" | "ready" | "error";
type ResponseModalState = { status: AdminSeniorVoResponseStatus; title: string; message: string };

const ROLE_FILTERS: SeniorVoRoleFilter[] = ["All", "Senior VO", "VO Member"];
const STATUS_FILTERS: SeniorVoStatusFilter[] = ["All", "Active", "Inactive"];
const DEFAULT_ADD_MEMBER_FORM_VALUES: AdminSeniorVoAddMemberFormValues = { memberName: "", memberEmail: "" };
const DEFAULT_ADD_TEAM_FORM_VALUES: AdminSeniorVoAddTeamFormValues = { teamName: "", seniorVoName: "", seniorVoEmail: "" };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AdminSeniorVoUsersPanel = () => {
  const theme = useAtomValue(themeAtom);
  const [teams, setTeams] = useState<AdminSeniorVoTeam[]>([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<SeniorVoRoleFilter>("All");
  const [statusFilter, setStatusFilter] = useState<SeniorVoStatusFilter>("All");
  const [expandedTeamIds, setExpandedTeamIds] = useState<Record<string, boolean>>({});
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedTeamForMemberAdd, setSelectedTeamForMemberAdd] = useState<AdminSeniorVoTeam | null>(null);
  const [addMemberFormValues, setAddMemberFormValues] =
    useState<AdminSeniorVoAddMemberFormValues>(DEFAULT_ADD_MEMBER_FORM_VALUES);
  const [addMemberFormErrors, setAddMemberFormErrors] = useState<AdminSeniorVoAddMemberFormErrors>({});
  const [isAddMemberSaving, setIsAddMemberSaving] = useState(false);
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false);
  const [addTeamFormValues, setAddTeamFormValues] =
    useState<AdminSeniorVoAddTeamFormValues>(DEFAULT_ADD_TEAM_FORM_VALUES);
  const [addTeamFormErrors, setAddTeamFormErrors] = useState<AdminSeniorVoAddTeamFormErrors>({});
  const [isAddTeamSaving, setIsAddTeamSaving] = useState(false);
  const [savingMemberRoleKey, setSavingMemberRoleKey] = useState<string | null>(null);
  const [openMemberActionKey, setOpenMemberActionKey] = useState<string | null>(null);
  const [responseModalState, setResponseModalState] = useState<ResponseModalState | null>(null);

  const loadTeams = useCallback(async () => {
    log("AdminSeniorVoUsersPanel - loadTeams");
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const response = await getAdminSeniorVoTeams();
      log("STATE - applySeniorVoTeams", { count: response.teams.length });
      setTeams(response.teams);
      setExpandedTeamIds((current) =>
        response.teams.reduce<Record<string, boolean>>((acc, team, index) => {
          acc[team.id] = current[team.id] ?? index === 0;
          return acc;
        }, {}),
      );
      setLoadingState("ready");
    } catch (error) {
      logE(error);
      setErrorMessage(getAdminSeniorVoErrorMessage(error));
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    if (!openMemberActionKey) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('[data-member-action-menu-root="true"]')) {
        return;
      }

      setOpenMemberActionKey(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [openMemberActionKey]);

  const visibleTeams = useMemo(
    () =>
      teams
        .map((team) => {
          const normalizedSearch = search.trim().toLowerCase();
          const teamHeaderMatches =
            normalizedSearch.length === 0 ||
            team.name.toLowerCase().includes(normalizedSearch);
          const members = team.members.filter((member) => {
            const memberMatchesText =
              normalizedSearch.length === 0 ||
              teamHeaderMatches ||
              member.name.toLowerCase().includes(normalizedSearch) ||
              member.email.toLowerCase().includes(normalizedSearch);
            const memberMatchesRole =
              roleFilter === "All" ||
              (roleFilter === "Senior VO" && member.role === "Senior VO") ||
              (roleFilter === "VO Member" && member.role === "VO");
            const memberMatchesStatus = statusFilter === "All" || member.status === statusFilter;
            return memberMatchesText && memberMatchesRole && memberMatchesStatus;
          });

          return {
            ...team,
            visibleMembers: members,
            visibleSeniorVos: members.filter((member) => member.role === "Senior VO"),
            visibleVoMembers: members.filter((member) => member.role === "VO"),
            matches: teamHeaderMatches || members.length > 0,
          };
        })
        .filter((team) => team.matches),
    [roleFilter, search, statusFilter, teams],
  );

  const openAddMemberModal = useCallback((team: AdminSeniorVoTeam) => {
    log("AdminSeniorVoUsersPanel - openAddMemberModal", { teamId: team.id });
    setSelectedTeamForMemberAdd(team);
    setAddMemberFormValues(DEFAULT_ADD_MEMBER_FORM_VALUES);
    setAddMemberFormErrors({});
  }, []);

  const closeAddMemberModal = useCallback(() => {
    if (isAddMemberSaving) {
      return;
    }

    setSelectedTeamForMemberAdd(null);
    setAddMemberFormValues(DEFAULT_ADD_MEMBER_FORM_VALUES);
    setAddMemberFormErrors({});
  }, [isAddMemberSaving]);

  const openAddTeamModal = useCallback(() => {
    setIsAddTeamModalOpen(true);
    setAddTeamFormValues(DEFAULT_ADD_TEAM_FORM_VALUES);
    setAddTeamFormErrors({});
  }, []);

  const closeAddTeamModal = useCallback(() => {
    if (isAddTeamSaving) {
      return;
    }

    setIsAddTeamModalOpen(false);
    setAddTeamFormValues(DEFAULT_ADD_TEAM_FORM_VALUES);
    setAddTeamFormErrors({});
  }, [isAddTeamSaving]);

  const updateAddMemberField = useCallback((field: keyof AdminSeniorVoAddMemberFormValues, value: string) => {
    setAddMemberFormValues((current) => ({ ...current, [field]: value }));
    setAddMemberFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[field];
      return nextErrors;
    });
  }, []);

  const updateAddTeamField = useCallback((field: keyof AdminSeniorVoAddTeamFormValues, value: string) => {
    setAddTeamFormValues((current) => ({ ...current, [field]: value }));
    setAddTeamFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[field];
      return nextErrors;
    });
  }, []);

  const validateAddMemberForm = useCallback(
    (_team: AdminSeniorVoTeam | null, values: AdminSeniorVoAddMemberFormValues): AdminSeniorVoAddMemberFormErrors => {
      const nextErrors: AdminSeniorVoAddMemberFormErrors = {};

      if (!values.memberName.trim()) {
        nextErrors.memberName = "Member name is required.";
      }

      if (!values.memberEmail.trim()) {
        nextErrors.memberEmail = "Member email is required.";
      } else if (!EMAIL_PATTERN.test(values.memberEmail.trim())) {
        nextErrors.memberEmail = "Enter a valid email address.";
      }

      return nextErrors;
    },
    [],
  );

  const validateAddTeamForm = useCallback((values: AdminSeniorVoAddTeamFormValues): AdminSeniorVoAddTeamFormErrors => {
    const nextErrors: AdminSeniorVoAddTeamFormErrors = {};

    if (!values.teamName.trim()) {
      nextErrors.teamName = "Team name is required.";
    }

    if (!values.seniorVoName.trim()) {
      nextErrors.seniorVoName = "Senior VO name is required.";
    }

    if (!values.seniorVoEmail.trim()) {
      nextErrors.seniorVoEmail = "Senior VO email is required.";
    } else if (!EMAIL_PATTERN.test(values.seniorVoEmail.trim())) {
      nextErrors.seniorVoEmail = "Enter a valid email address.";
    }

    return nextErrors;
  }, []);

  const handleAddMemberSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const validationErrors = validateAddMemberForm(selectedTeamForMemberAdd, addMemberFormValues);
      setAddMemberFormErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0 || !selectedTeamForMemberAdd) {
        return;
      }

      setIsAddMemberSaving(true);

      try {
        const response = await addAdminSeniorVoMember({
          teamId: selectedTeamForMemberAdd.id,
          teamName: selectedTeamForMemberAdd.name,
          memberName: addMemberFormValues.memberName.trim(),
          memberEmail: addMemberFormValues.memberEmail.trim(),
        });

        setSelectedTeamForMemberAdd(null);
        setAddMemberFormValues(DEFAULT_ADD_MEMBER_FORM_VALUES);
        setAddMemberFormErrors({});
        setResponseModalState({
          status: response.success ? "success" : "error",
          title: response.success ? "Member Added" : "Unable To Add Member",
          message: response.message,
        });
      } catch (error) {
        logE(error);
        setSelectedTeamForMemberAdd(null);
        setResponseModalState({
          status: "error",
          title: "Unable To Add Member",
          message: getAdminSeniorVoErrorMessage(error),
        });
      } finally {
        setIsAddMemberSaving(false);
      }
    },
    [addMemberFormValues, selectedTeamForMemberAdd, validateAddMemberForm],
  );

  const handleAddTeamSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const validationErrors = validateAddTeamForm(addTeamFormValues);
      setAddTeamFormErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        return;
      }

      setIsAddTeamSaving(true);

      try {
        const response = await addAdminSeniorVoTeam({
          teamName: addTeamFormValues.teamName.trim(),
          seniorVoName: addTeamFormValues.seniorVoName.trim(),
          seniorVoEmail: addTeamFormValues.seniorVoEmail.trim(),
        });

        setIsAddTeamModalOpen(false);
        setAddTeamFormValues(DEFAULT_ADD_TEAM_FORM_VALUES);
        setAddTeamFormErrors({});
        setResponseModalState({
          status: response.success ? "success" : "error",
          title: response.success ? "Team Added" : "Unable To Add Team",
          message: response.message,
        });
      } catch (error) {
        logE(error);
        setIsAddTeamModalOpen(false);
        setResponseModalState({
          status: "error",
          title: "Unable To Add Team",
          message: getAdminSeniorVoErrorMessage(error),
        });
      } finally {
        setIsAddTeamSaving(false);
      }
    },
    [addTeamFormValues, validateAddTeamForm],
  );

  const closeResponseModal = useCallback(() => {
    setResponseModalState(null);
    void loadTeams();
  }, [loadTeams]);

  const handleUpdateMemberRole = useCallback(
    async (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, role: "Senior VO" | "VO") => {
      const memberKey = `${team.id}:${member.id}`;

      setOpenMemberActionKey(null);
      setSavingMemberRoleKey(memberKey);

      try {
        const response = await updateAdminSeniorVoMemberRole({
          teamId: team.id,
          teamName: team.name,
          memberName: member.name,
          memberEmail: member.email,
          role,
        });

        setResponseModalState({
          status: response.success ? "success" : "error",
          title: response.success ? "Member Role Updated" : "Unable To Update Member Role",
          message: response.message,
        });
      } catch (error) {
        logE(error);
        setResponseModalState({
          status: "error",
          title: "Unable To Update Member Role",
          message: getAdminSeniorVoErrorMessage(error),
        });
      } finally {
        setSavingMemberRoleKey(null);
      }
    },
    [],
  );

  const handleUpdateMemberStatus = useCallback(
    async (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, isActive: boolean) => {
      const memberKey = `${team.id}:${member.id}`;

      setOpenMemberActionKey(null);
      setSavingMemberRoleKey(memberKey);

      try {
        const response = await updateAdminSeniorVoMemberStatus({
          teamId: team.id,
          teamName: team.name,
          memberName: member.name,
          memberEmail: member.email,
          isActive,
        });

        setResponseModalState({
          status: response.success ? "success" : "error",
          title: response.success ? "Member Status Updated" : "Unable To Update Member Status",
          message: response.message,
        });
      } catch (error) {
        logE(error);
        setResponseModalState({
          status: "error",
          title: "Unable To Update Member Status",
          message: getAdminSeniorVoErrorMessage(error),
        });
      } finally {
        setSavingMemberRoleKey(null);
      }
    },
    [],
  );

  return (
    <main
      data-test="admin-senior-vo-page"
      className={twMerge("flex min-h-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#FCFDFC]">
        <AdminVoyagePnlViewTabs />

        <div className={twMerge(adminStyles.page.pagePadding, "min-h-0 flex-1 overflow-y-auto")}>
          <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6">
            <section className="flex flex-col gap-6">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <h1 className="text-[1.7rem] font-semibold leading-tight text-[#102A43]">Senior VO Users & Teams</h1>
                  <p className="mt-1.5 text-sm text-[#52606D]">Manage team-based Senior VO approvals and membership</p>
                </div>

                <div className="flex flex-col gap-3 lg:flex-row xl:items-center">
                  <SearchField value={search} onChange={setSearch} />
                  <FilterSelect
                    ariaLabel="Filter by role"
                    value={roleFilter}
                    options={ROLE_FILTERS}
                    onChange={(value) => setRoleFilter(value as SeniorVoRoleFilter)}
                  />
                  <FilterSelect
                    ariaLabel="Filter by status"
                    value={statusFilter}
                    options={STATUS_FILTERS}
                    onChange={(value) => setStatusFilter(value as SeniorVoStatusFilter)}
                  />
                  <button
                    type="button"
                    onClick={openAddTeamModal}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#0B8A7C] px-4 text-xs font-semibold text-white shadow-[0_18px_40px_-24px_rgba(11,138,124,0.9)] transition-colors hover:bg-[#0A796D]"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add Team
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <TeamsStatus
                  loadingState={loadingState}
                  errorMessage={errorMessage}
                  onRetry={loadTeams}
                  hasTeams={teams.length > 0}
                />
                {loadingState !== "loading" &&
                  (visibleTeams.length === 0 ? (
                    <EmptyState />
                  ) : (
                    visibleTeams.map((team) => (
                      <TeamCard
                        key={team.id}
                        team={team}
                        isExpanded={Boolean(expandedTeamIds[team.id])}
                        onAddMember={openAddMemberModal}
                        onUpdateMemberRole={handleUpdateMemberRole}
                        onUpdateMemberStatus={handleUpdateMemberStatus}
                        savingMemberRoleKey={savingMemberRoleKey}
                        openMemberActionKey={openMemberActionKey}
                        onToggleMemberActions={(memberKey) =>
                          setOpenMemberActionKey((current) => (current === memberKey ? null : memberKey))
                        }
                        onToggle={() =>
                          setExpandedTeamIds((current) => ({
                            ...current,
                            [team.id]: !current[team.id],
                          }))
                        }
                      />
                    ))
                  ))}
              </div>

              <InfoBanner />
            </section>
          </div>
        </div>
      </div>

      {selectedTeamForMemberAdd && (
        <AdminSeniorVoAddMemberModal
          team={selectedTeamForMemberAdd}
          values={addMemberFormValues}
          errors={addMemberFormErrors}
          saving={isAddMemberSaving}
          onCancel={closeAddMemberModal}
          onChange={updateAddMemberField}
          onSubmit={handleAddMemberSubmit}
        />
      )}

      {isAddTeamModalOpen && (
        <AdminSeniorVoAddTeamModal
          values={addTeamFormValues}
          errors={addTeamFormErrors}
          saving={isAddTeamSaving}
          onCancel={closeAddTeamModal}
          onChange={updateAddTeamField}
          onSubmit={handleAddTeamSubmit}
        />
      )}

      {responseModalState && (
        <AdminSeniorVoResponseModal
          status={responseModalState.status}
          title={responseModalState.title}
          message={responseModalState.message}
          onClose={closeResponseModal}
        />
      )}
    </main>
  );
};

const SearchField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <label className="relative block min-w-[260px] flex-1 xl:w-[300px] xl:flex-none">
    <span className="sr-only">Search teams or users</span>
    <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7B8794]" />
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Search teams or users..."
      className="h-10 w-full rounded-xl border border-[#D9E2EC] bg-white pl-11 pr-4 text-xs text-[#102A43] outline-none transition-colors placeholder:text-[#9AA5B1] focus:border-[#0B8A7C]"
    />
  </label>
);

const FilterSelect = ({
  ariaLabel,
  value,
  options,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) => (
  <label className="relative block min-w-[170px]">
    <span className="sr-only">{ariaLabel}</span>
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full appearance-none rounded-xl border border-[#D9E2EC] bg-white px-4 pr-10 text-xs text-[#102A43] outline-none transition-colors focus:border-[#0B8A7C]"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {ariaLabel === "Filter by role" ? `Role: ${option}` : `Status: ${option}`}
        </option>
      ))}
    </select>
    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7B8794]" />
  </label>
);

const TeamsStatus = ({
  loadingState,
  errorMessage,
  onRetry,
  hasTeams,
}: {
  loadingState: LoadingState;
  errorMessage: string | null;
  onRetry: () => void;
  hasTeams: boolean;
}) => {
  if (loadingState === "loading") {
    return (
      <div className="rounded-[20px] border border-[#D9E2EC] bg-white px-5 py-4 text-xs text-[#52606D]">
        Loading Senior VO teams...
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex flex-col gap-3 rounded-[20px] border border-[#F5C2C7] bg-[#FFF1F1] px-5 py-4 text-xs text-[#8F0002]">
        <span>{errorMessage}</span>
        <div>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-9 items-center rounded-xl border border-[#BF0002] px-4 text-xs font-semibold text-[#BF0002] transition-colors hover:bg-white/70"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!hasTeams) {
    return (
      <div className="rounded-[20px] border border-[#D9E2EC] bg-white px-5 py-4 text-xs text-[#52606D]">
        No Senior VO teams were returned by the backend.
      </div>
    );
  }

  return null;
};

const TeamCard = ({
  team,
  isExpanded,
  onAddMember,
  onUpdateMemberRole,
  onUpdateMemberStatus,
  savingMemberRoleKey,
  openMemberActionKey,
  onToggleMemberActions,
  onToggle,
}: {
  team: AdminSeniorVoTeam & {
    visibleMembers: AdminSeniorVoMember[];
    visibleSeniorVos: AdminSeniorVoMember[];
    visibleVoMembers: AdminSeniorVoMember[];
  };
  isExpanded: boolean;
  onAddMember: (team: AdminSeniorVoTeam) => void;
  onUpdateMemberRole: (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, role: "Senior VO" | "VO") => void;
  onUpdateMemberStatus: (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, isActive: boolean) => void;
  savingMemberRoleKey: string | null;
  openMemberActionKey: string | null;
  onToggleMemberActions: (memberKey: string) => void;
  onToggle: () => void;
}) => {
  const seniorVoCount = team.members.filter((member) => member.role === "Senior VO").length;
  const activeSeniorVoCount = team.members.filter(
    (member) => member.role === "Senior VO" && member.status === "Active",
  ).length;
  const voCount = team.members.filter((member) => member.role === "VO").length;

  return (
    <article className="rounded-[24px] border border-[#E4ECE9] bg-white p-7 shadow-[0_24px_80px_-56px_rgba(16,42,67,0.35)]">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-start gap-4">
          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${team.name}` : `Expand ${team.name}`}
            onClick={onToggle}
            className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[#486581] transition-colors hover:bg-[#F0F7F6]"
          >
            {isExpanded ? <ChevronDownIcon className="h-5 w-5" /> : <ChevronRightIcon className="h-5 w-5" />}
          </button>
          <div>
            <h2 className="text-[1.45rem] font-semibold leading-tight text-[#102A43]">{team.name}</h2>
            <p className="mt-1 text-xs text-[#52606D]">
              <span className="font-semibold text-[#243B53]">Approval scope:</span> Senior VOs can approve requests from anyone in this team
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 xl:items-end">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#52606D]">
            <span>{team.members.length} Total Members</span>
            <span className="text-[#BCCCDC]">&bull;</span>
            <span>{seniorVoCount} Senior VOs</span>
            <span className="text-[#BCCCDC]">&bull;</span>
            <span>{voCount} VOs</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onAddMember(team)}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#CFE4E1] bg-white px-4 text-xs font-semibold text-[#0B7C71] transition-colors hover:bg-[#F2FBFA]"
            >
              <PlusIcon className="h-5 w-5" />
              Add Member
            </button>
          </div>
        </div>
      </header>

      {isExpanded && (
        <div className="mt-6 space-y-4">
          <MemberSection
            title={`SENIOR VOS (${team.visibleSeniorVos.length})`}
            subtitle="Users with SeniorVO privileges"
            icon={<ShieldCheckIcon className="h-5 w-5" />}
            variant="senior"
            team={team}
            members={team.visibleSeniorVos}
            onUpdateMemberRole={onUpdateMemberRole}
            onUpdateMemberStatus={onUpdateMemberStatus}
            savingMemberRoleKey={savingMemberRoleKey}
            activeSeniorVoCount={activeSeniorVoCount}
            openMemberActionKey={openMemberActionKey}
            onToggleMemberActions={onToggleMemberActions}
          />
          <MemberSection
            title={`VO MEMBERS (${team.visibleVoMembers.length})`}
            subtitle="Regular VO members"
            icon={<UserGroupIcon className="h-5 w-5" />}
            variant="member"
            team={team}
            members={team.visibleVoMembers}
            onUpdateMemberRole={onUpdateMemberRole}
            onUpdateMemberStatus={onUpdateMemberStatus}
            savingMemberRoleKey={savingMemberRoleKey}
            activeSeniorVoCount={activeSeniorVoCount}
            openMemberActionKey={openMemberActionKey}
            onToggleMemberActions={onToggleMemberActions}
          />
        </div>
      )}
    </article>
  );
};

const MemberSection = ({
  title,
  subtitle,
  icon,
  variant,
  team,
  members,
  onUpdateMemberRole,
  onUpdateMemberStatus,
  savingMemberRoleKey,
  activeSeniorVoCount,
  openMemberActionKey,
  onToggleMemberActions,
}: {
  title: string;
  subtitle: string;
  icon: ReactNode;
  variant: "senior" | "member";
  team: AdminSeniorVoTeam;
  members: AdminSeniorVoMember[];
  onUpdateMemberRole: (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, role: "Senior VO" | "VO") => void;
  onUpdateMemberStatus: (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, isActive: boolean) => void;
  savingMemberRoleKey: string | null;
  activeSeniorVoCount: number;
  openMemberActionKey: string | null;
  onToggleMemberActions: (memberKey: string) => void;
}) => (
  <section className="rounded-[20px] border border-[#E7EEEC] bg-[#FCFEFD] p-6">
    <div className="flex items-start gap-3">
      <div
        className={twMerge(
          "flex h-11 w-11 items-center justify-center rounded-full",
          variant === "senior" ? "bg-[#E1F6F2] text-[#0B8A7C]" : "bg-[#EEF2F7] text-[#52606D]",
        )}
      >
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold tracking-[0.01em] text-[#102A43]">{title}</h3>
        <p className="mt-1 text-xs text-[#52606D]">{subtitle}</p>
      </div>
    </div>

    <div className="mt-5 overflow-hidden rounded-[18px] border border-[#E3EEEB]">
      {members.length === 0 ? (
        <div className="bg-white px-5 py-6 text-xs text-[#7B8794]">No matching members in this section.</div>
      ) : (
        members.map((member, index) => (
          <MemberRow
            key={member.id}
            team={team}
            member={member}
            variant={variant}
            activeSeniorVoCount={activeSeniorVoCount}
            isLast={index === members.length - 1}
            onUpdateMemberRole={onUpdateMemberRole}
            onUpdateMemberStatus={onUpdateMemberStatus}
            isSavingRole={savingMemberRoleKey === `${team.id}:${member.id}`}
            disableActions={Boolean(savingMemberRoleKey)}
            isActionMenuOpen={openMemberActionKey === `${team.id}:${member.id}`}
            onToggleActionMenu={onToggleMemberActions}
          />
        ))
      )}
    </div>
  </section>
);

const MemberRow = ({
  team,
  member,
  variant,
  activeSeniorVoCount,
  isLast,
  onUpdateMemberRole,
  onUpdateMemberStatus,
  isSavingRole,
  disableActions,
  isActionMenuOpen,
  onToggleActionMenu,
}: {
  team: AdminSeniorVoTeam;
  member: AdminSeniorVoMember;
  variant: "senior" | "member";
  activeSeniorVoCount: number;
  isLast: boolean;
  onUpdateMemberRole: (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, role: "Senior VO" | "VO") => void;
  onUpdateMemberStatus: (team: AdminSeniorVoTeam, member: AdminSeniorVoMember, isActive: boolean) => void;
  isSavingRole: boolean;
  disableActions: boolean;
  isActionMenuOpen: boolean;
  onToggleActionMenu: (memberKey: string) => void;
}) => {
  const memberKey = `${team.id}:${member.id}`;
  const canRevokeSeniorVoRole = member.role !== "Senior VO" || activeSeniorVoCount > 1;
  const canMarkInactive = member.status === "Active" && (member.role !== "Senior VO" || activeSeniorVoCount > 1);

  return (
    <div
      className={twMerge(
        "flex flex-col gap-4 px-5 py-4 md:flex-row md:items-center md:justify-between",
        variant === "senior" ? "bg-[#F2FCFA]" : "bg-white",
        !isLast && "border-b border-[#E7EEEC]",
      )}
    >
      <div className="flex min-w-0 items-center gap-4">
        <MemberAvatar name={member.name} variant={variant} />
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-[#102A43]">{member.name}</p>
          <p className="truncate text-xs text-[#52606D]">{member.email || "-"}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        {member.status === "Inactive" && (
          <span className="inline-flex rounded-full border border-[#F7C6C5] bg-[#FFF1F1] px-3 py-1 text-[11px] font-semibold text-[#B42318]">
            Inactive
          </span>
        )}
        {variant === "senior" && (
          <span className="inline-flex rounded-full bg-[#0B8A7C] px-3 py-1 text-[11px] font-semibold text-white">
            Senior VO
          </span>
        )}
        <div className="relative" data-member-action-menu-root="true">
          <button
            type="button"
            aria-label={`More actions for ${member.name}`}
            aria-expanded={isActionMenuOpen}
            onClick={() => onToggleActionMenu(memberKey)}
            disabled={disableActions}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#52606D] transition-colors hover:bg-[#F7FAFC] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
          {isActionMenuOpen && (
            <div className="absolute right-full top-1/2 z-10 mr-2 min-w-[210px] -translate-y-1/2 rounded-xl border border-[#D9E2EC] bg-white p-1.5 shadow-[0_20px_50px_-28px_rgba(16,42,67,0.4)]">
              {member.status === "Active" ? (
                <>
                  {member.role === "Senior VO" ? (
                    <ActionMenuItem
                      label="Revoke Senior VO Role"
                      disabled={!canRevokeSeniorVoRole}
                      onClick={() => onUpdateMemberRole(team, member, "VO")}
                    />
                  ) : (
                    <ActionMenuItem
                      label="Grant Senior VO Role"
                      onClick={() => onUpdateMemberRole(team, member, "Senior VO")}
                    />
                  )}
                  <ActionMenuItem
                    label="Mark User Inactive"
                    disabled={!canMarkInactive}
                    tone="danger"
                    onClick={() => onUpdateMemberStatus(team, member, false)}
                  />
                </>
              ) : (
                <ActionMenuItem label="User already inactive" disabled />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ActionMenuItem = ({
  label,
  disabled = false,
  tone = "default",
  onClick,
}: {
  label: string;
  disabled?: boolean;
  tone?: "default" | "danger";
  onClick?: () => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={twMerge(
      "flex w-full items-center rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors",
      tone === "danger" ? "text-[#B42318] hover:bg-[#FFF1F1]" : "text-[#102A43] hover:bg-[#F7FAFC]",
      disabled && "cursor-not-allowed text-[#9AA5B1] hover:bg-transparent",
    )}
  >
    {label}
  </button>
);

const MemberAvatar = ({ name, variant }: { name: string; variant: "senior" | "member" }) => (
  <div
    className={twMerge(
      "flex h-11 w-11 flex-none items-center justify-center rounded-full text-sm font-bold text-white",
      variant === "senior" ? "bg-[#0B8A7C]" : "bg-[#7B8794]",
    )}
  >
    {name
      .split(" ")
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase()}
  </div>
);

const EmptyState = () => (
  <div className="rounded-[24px] border border-dashed border-[#C9D7E4] bg-white px-6 py-12 text-center shadow-[0_20px_60px_-48px_rgba(16,42,67,0.35)]">
    <h2 className="text-lg font-semibold text-[#102A43]">No teams match the current filters</h2>
    <p className="mt-2 text-xs text-[#52606D]">Try broadening the search or switching the role and status filters.</p>
  </div>
);

const InfoBanner = () => (
  <aside className="flex items-start gap-4 rounded-[20px] border border-[#D9EBF8] bg-[#EEF8FF] px-5 py-5 text-[#102A43]">
    <InformationCircleIcon className="mt-0.5 h-6 w-6 flex-none text-[#1D72D2]" />
    <div>
      <p className="text-sm font-semibold">Any member with the Senior VO role can approve requests for their whole team.</p>
      <p className="mt-1 text-xs text-[#52606D]">Team membership is now driven by the shared team structure instead of one-to-one Senior VO mappings.</p>
    </div>
  </aside>
);

export default AdminSeniorVoUsersPanel;
